import { IBulkApprovalResult } from '@common/interfaces/atlasai'
import { hasRequestFailed } from '../../helpers/redux'

/**
 * How many approvals a bulk action has in flight at once.
 *
 * A bulk run replays the single-item endpoint per approval rather than posting
 * one batch request. Each approval executes a WRITE_DANGEROUS service and a
 * single bot run can queue 30+, so one request covering all of them would stay
 * open for minutes — and if it timed out mid-batch the user would have no way to
 * tell which writes actually happened. Per-item requests keep every call the
 * size it already is, and make each outcome attributable to its own card.
 */
export const BULK_APPROVAL_CONCURRENCY = 4

/**
 * Codes that must not be flattened into a per-card failure.
 *
 * apiMiddleware dispatches `sessionExpired()` / `permissionDenied()` only from
 * its promise-rejection branch, so a bulk helper that catches everything would
 * leave an expired session signed in and a forbidden user on the page — where
 * clicking Approve on the same card individually signs them out or sends them
 * home. These abort the batch and are re-thrown so that branch runs.
 */
const GLOBALLY_HANDLED_ERROR_CODES = ['AUTHORIZATION_ERROR', 'PERMISSION_DENIED']

/** Recorded against cards the batch never reached because it aborted. */
const NOT_ATTEMPTED_MESSAGE = 'Not attempted — the batch stopped.'

/** A bulk rejection, carrying whatever the batch had resolved before it aborted. */
export type IBulkApprovalAbort = Error & { bulkResult: IBulkApprovalResult }

const isGloballyHandled = (err: unknown): boolean =>
  GLOBALLY_HANDLED_ERROR_CODES.includes((err as { code?: string } | undefined)?.code ?? '')

const errorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message
  return typeof err === 'string' ? err : 'Unknown error'
}

/**
 * The globally-handled error, with the partial batch attached to it.
 *
 * The error object has to reach apiMiddleware intact — its `code` and
 * `tokenGeneration` are what `sessionExpired()` keys off — so the partial result
 * rides along on it rather than wrapping it. Cards the abort left unattempted are
 * recorded as failures, so every selected id still lands in exactly one bucket
 * and the panel never implies a write happened when it did not.
 */
const abortWith = (
  err: unknown,
  approvalIds: string[],
  resolved: IBulkApprovalResult,
): IBulkApprovalAbort => {
  const reached = new Set([
    ...resolved.succeededIds,
    ...resolved.failures.map((failure) => failure.approvalId),
  ])
  const bulkResult: IBulkApprovalResult = {
    succeededIds: resolved.succeededIds,
    failures: [
      ...resolved.failures,
      ...approvalIds
        .filter((approvalId) => !reached.has(approvalId))
        .map((approvalId) => ({ approvalId, message: NOT_ATTEMPTED_MESSAGE })),
    ],
  }
  return Object.assign(err instanceof Error ? err : new Error(errorMessage(err)), { bulkResult })
}

/**
 * Resolves every approval in bounded windows, collecting outcomes per id so one
 * card failing does not abort the rest of the batch.
 *
 * Rejects only for the codes in `GLOBALLY_HANDLED_ERROR_CODES`, which have to
 * reach apiMiddleware to be handled; the rejection carries the partial result.
 */
export const resolveApprovalsInBatches = async (
  approvalIds: string[],
  resolveOne: (approvalId: string) => Promise<unknown>,
): Promise<IBulkApprovalResult> => {
  /** One approval's outcome; `error` is null when it resolved cleanly. */
  type Outcome = { approvalId: string; error: unknown }

  const succeededIds: string[] = []
  const failures: IBulkApprovalResult['failures'] = []

  const resolveToOutcome = async (approvalId: string): Promise<Outcome> => {
    try {
      await resolveOne(approvalId)
      return { approvalId, error: null }
    } catch (err) {
      return { approvalId, error: err ?? new Error('Unknown error') }
    }
  }

  for (let start = 0; start < approvalIds.length; start += BULK_APPROVAL_CONCURRENCY) {
    const window = approvalIds.slice(start, start + BULK_APPROVAL_CONCURRENCY)
    // Windows are awaited one after another on purpose — that is what bounds the
    // fan-out. See BULK_APPROVAL_CONCURRENCY.
    const outcomes = await Promise.all(window.map(resolveToOutcome))
    outcomes.forEach(({ approvalId, error }) => {
      if (error === null) succeededIds.push(approvalId)
      else failures.push({ approvalId, message: errorMessage(error) })
    })

    // Recorded first, thrown second: the window's other requests already went out,
    // so their outcomes are real and belong in the partial result.
    const fatal = outcomes.find(({ error }) => isGloballyHandled(error))
    if (fatal) throw abortWith(fatal.error, approvalIds, { succeededIds, failures })
  }

  return { succeededIds, failures }
}

/**
 * The batch outcome off an awaited bulk action, or null if there is none.
 *
 * A FAILURE here is the batch aborting on an error apiMiddleware handles globally
 * — an expired session, a card belonging to another user — rather than an
 * individual approval failing, which `resolveApprovalsInBatches` keeps in-band.
 * The abort still carries what it had resolved up to that point, so the panel
 * reports the partial run instead of claiming nothing happened.
 * See `hasRequestFailed`: apiMiddleware resolves failures with the action.
 */
export const bulkResultFrom = (result: unknown): IBulkApprovalResult | null => {
  if (hasRequestFailed(result)) {
    return (
      (result as { error?: Partial<IBulkApprovalAbort> } | undefined)?.error?.bulkResult ?? null
    )
  }
  return (result as { data?: IBulkApprovalResult } | undefined)?.data ?? null
}
